import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { SmartCMRLogo } from '../components/brand/SmartCMRLogo';
import { Truck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import LanguageSelector from '../components/common/LanguageSelector';

const LoginPage = () => {
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const cleanEmail = formData.email.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const result = await login(cleanEmail, formData.password, formData.rememberMe);
      if (result.success) {
        sessionStorage.removeItem('verification_email');
        toast.success(t('auth.loginSuccess'));
        navigate('/dashboard', { replace: true });
      } else if (result.errorKey === 'email_not_verified') {
        // Store email in sessionStorage so VerificationSentPage survives Android Chrome backgrounding
        sessionStorage.setItem('verification_email', cleanEmail);
        toast.error(t('auth.emailNotVerified') || 'Je account is nog niet geverifieerd. Controleer je e-mail.');
        navigate('/verification-sent', {
          state: { email: cleanEmail },
          replace: true
        });
      } else if (result.errorKey === 'account_deactivated') {
        toast.error(t('auth.accountDeactivated'));
      } else {
        toast.error(t('auth.invalidCredentials'));
      }
    } catch {
      toast.error(t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-white flex flex-col">
      <header 
        className="p-4 flex justify-between items-center"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
      >
        <Link to="/" className="flex items-center space-x-2">
          <SmartCMRLogo />
        </Link>
        <LanguageSelector />
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid="login-card">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-2xl">{t('auth.loginTitle')}</CardTitle>
            <CardDescription>{t('auth.loginDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
              </div>

              <div className="flex items-center" style={{ gap: '8px' }}>
                <Checkbox
                  id="remember-me"
                  checked={formData.rememberMe}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rememberMe: !!checked }))}
                  data-testid="remember-me-checkbox"
                />
                <label htmlFor="remember-me" className="text-sm text-slate-600 select-none cursor-pointer">
                  {t('auth.rememberMe')}
                </label>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.loading')}</>
                ) : (
                  t('auth.loginButton')
                )}
              </Button>

              <p className="text-center text-sm text-slate-500">
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="text-blue-600 hover:underline font-medium" data-testid="register-link">
                  {t('auth.register')}
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
